import { ArrowLeft, ArrowRight, CheckCircle2, MessageCircle, ShieldCheck } from "lucide-react";
import { useRef, useState } from "react";
import type { BalconyProfile, DesignItem } from "../../data/model";
import { estimateTotal, plantCount } from "../../lib/calculations";
import { formatMoney } from "../../lib/format";

interface Props {
  profile: BalconyProfile;
  design: DesignItem[];
  packageId: string;
  onBack: () => void;
  onSuccess: (code: string) => void;
}

interface FormValue {
  name: string;
  phone: string;
  city: string;
  time: string;
  note: string;
  consent: boolean;
}

export function ConsultationForm({ profile, design, packageId, onBack, onSuccess }: Props) {
  const [form, setForm] = useState<FormValue>({ name: "", phone: "", city: "TP. Hồ Chí Minh", time: "19:00–21:00", note: "", consent: false });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const errorRef = useRef<HTMLDivElement>(null);
  const total = estimateTotal(design, packageId);

  const patch = <K extends keyof FormValue>(key: K, value: FormValue[K]) => setForm((current) => ({ ...current, [key]: value }));

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    const next: Record<string, string> = {};
    if (form.name.trim().length < 2) next.name = "Nhập ít nhất 2 ký tự.";
    if (!/^0\d{9}$/.test(form.phone.replace(/\s/g, ""))) next.phone = "Nhập số Việt Nam gồm 10 chữ số, bắt đầu bằng 0.";
    if (!form.consent) next.consent = "Bạn cần xác nhận đây là yêu cầu mô phỏng.";
    setErrors(next);
    if (Object.keys(next).length) {
      window.setTimeout(() => errorRef.current?.focus(), 0);
      return;
    }
    const checksum = (profile.widthCm + profile.depthCm + plantCount(design) * 17 + form.phone.split("").reduce((sum, digit) => sum + Number(digit || 0), 0)) % 10000;
    onSuccess(`RN-260730-${String(checksum).padStart(4, "0")}`);
  };

  return (
    <div className="consult-page">
      <div className="consult-heading">
        <button className="back-link" type="button" onClick={onBack}><ArrowLeft aria-hidden="true" /> Quay lại dự toán</button>
        <span className="eyebrow">Bước 4/4 · yêu cầu mô phỏng</span>
        <h1>Mang bản phác vào một cuộc trò chuyện.</h1>
        <p>Form này chỉ chạy trong trình duyệt. Không có cuộc gọi, email hay dữ liệu nào được gửi đi.</p>
      </div>
      <div className="consult-layout">
        <form className="consult-form" onSubmit={submit} noValidate>
          {Object.keys(errors).length > 0 && (
            <div className="error-summary" role="alert" tabIndex={-1} ref={errorRef}>
              <strong>Còn vài chỗ cần bạn kiểm tra.</strong>
              <ul>{Object.values(errors).map((error) => <li key={error}>{error}</li>)}</ul>
            </div>
          )}
          <div className="form-grid">
            <label>
              <span>Họ và tên</span>
              <input value={form.name} onChange={(event) => patch("name", event.target.value)} aria-invalid={Boolean(errors.name)} aria-describedby={errors.name ? "name-error" : undefined} placeholder="Ví dụ: Minh An" />
              {errors.name && <small id="name-error" className="field-error">{errors.name}</small>}
            </label>
            <label>
              <span>Số điện thoại</span>
              <input inputMode="tel" value={form.phone} onChange={(event) => patch("phone", event.target.value)} aria-invalid={Boolean(errors.phone)} aria-describedby={errors.phone ? "phone-error" : undefined} placeholder="0901 234 567" />
              {errors.phone && <small id="phone-error" className="field-error">{errors.phone}</small>}
            </label>
            <label>
              <span>Thành phố</span>
              <select value={form.city} onChange={(event) => patch("city", event.target.value)}>
                <option>TP. Hồ Chí Minh</option><option>Hà Nội</option><option>Đà Nẵng</option><option>Thành phố khác</option>
              </select>
            </label>
            <label>
              <span>Khung giờ tiện nghe</span>
              <select value={form.time} onChange={(event) => patch("time", event.target.value)}>
                <option>09:00–11:00</option><option>14:00–17:00</option><option>19:00–21:00</option>
              </select>
            </label>
          </div>
          <label>
            <span>Điều bạn muốn tư vấn thêm <small>(không bắt buộc)</small></span>
            <textarea maxLength={280} value={form.note} onChange={(event) => patch("note", event.target.value)} placeholder="Ví dụ: muốn giữ chỗ cho giá phơi gập..." />
            <small className="char-count">{form.note.length}/280</small>
          </label>
          <label className={`consent-box ${errors.consent ? "has-error" : ""}`}>
            <input type="checkbox" checked={form.consent} onChange={(event) => patch("consent", event.target.checked)} />
            <span><strong>Tôi hiểu đây là yêu cầu mô phỏng</strong><small>Không có ai thực sự liên hệ và dữ liệu không rời khỏi trình duyệt.</small></span>
          </label>
          {errors.consent && <small className="field-error">{errors.consent}</small>}
          <button className="button button-primary button-large button-block" type="submit">Gửi yêu cầu mô phỏng <ArrowRight aria-hidden="true" /></button>
        </form>
        <aside className="consult-summary">
          <MessageCircle aria-hidden="true" />
          <span className="eyebrow">Brief gửi kèm</span>
          <h2>{profile.widthCm} × {profile.depthCm} cm</h2>
          <dl>
            <div><dt>Hướng</dt><dd>{profile.direction}</dd></div>
            <div><dt>Cây</dt><dd>{plantCount(design)} cây</dd></div>
            <div><dt>Dự toán</dt><dd>{formatMoney(total)}</dd></div>
            <div><dt>Ràng buộc</dt><dd>{[profile.hasKids && "trẻ nhỏ", profile.hasPets && "thú cưng"].filter(Boolean).join(", ") || "không"}</dd></div>
          </dl>
          <p><ShieldCheck aria-hidden="true" /> Snapshot này chỉ được cất local sau khi gửi.</p>
        </aside>
      </div>
    </div>
  );
}

export function ConsultationSuccess({
  code,
  profile,
  design,
  packageId,
  onReview,
  onNew,
}: {
  code: string;
  profile: BalconyProfile;
  design: DesignItem[];
  packageId: string;
  onReview: () => void;
  onNew: () => void;
}) {
  return (
    <div className="success-page">
      <div className="success-mark"><CheckCircle2 aria-hidden="true" /></div>
      <span className="eyebrow">Yêu cầu mô phỏng đã được tạo</span>
      <h1>Bản phác đã sẵn sàng cho buổi tư vấn.</h1>
      <p>Mã <strong>{code}</strong> chỉ tồn tại trong demo. Không có dữ liệu nào được gửi đi.</p>
      <div className="receipt-card">
        <div><span>Mã bản phác</span><strong>{code}</strong></div>
        <div><span>Ban công</span><strong>{profile.widthCm} × {profile.depthCm} cm</strong></div>
        <div><span>Số cây</span><strong>{plantCount(design)} cây</strong></div>
        <div><span>Dự toán</span><strong>{formatMoney(estimateTotal(design, packageId))}</strong></div>
      </div>
      <div className="success-actions">
        <button className="button button-secondary" type="button" onClick={onReview}>Xem lại bản phác</button>
        <button className="button button-primary" type="button" onClick={onNew}>Tạo phương án mới <ArrowRight aria-hidden="true" /></button>
      </div>
      <p className="success-safe"><ShieldCheck aria-hidden="true" /> Không backend · không email · không cuộc gọi</p>
    </div>
  );
}
